import { AsyncPipe } from '@angular/common';
import { Component, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { DateFormatPipe, MomentModule } from 'ngx-moment';
import { ItemDetails } from 'src/app/models/item-details';
import { KeyValuePairComponent } from 'src/app/shared/general/key-value-pair/key-value-pair.component';
import { AttributeValuePipe } from 'src/app/shared/item-attributes/attribute-value.pipe';
import { EquipmentModelPipe } from 'src/app/shared/pipes/equipment-model.pipe';
import { HlCurrencyPipe } from 'src/app/shared/pipes/hl-currency.pipe';
import { ItemExtrasComponent } from '../../../item-extras/item-extras.component';
import { ConfigService } from 'src/app/shared/services/config.service';

@Component({
  selector: 'app-key-details',
  standalone: true,
  imports: [
    AttributeValuePipe,
    EquipmentModelPipe,
    HlCurrencyPipe,
    ItemExtrasComponent,
    MomentModule,
    AsyncPipe,
    MatIconModule
  ],
  providers: [DateFormatPipe],
  template: `
    <div
      class="flex flex-col justify-start items-stretch gap-3 p-4 bg-white rounded-lg shadow-sm border border-slate-200"
    >
      <div
        class="flex flex-row justify-start items-center gap-x-3 pb-2 border-b border-slate-100"
      >
        <mat-icon class="text-indigo-500">info</mat-icon>
        <h2 class="text-lg font-bold text-slate-800">Key Details</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        @for (
          attribute of itemDetails().summaryAttributes;
          track attribute.name
        ) {
          @if (attribute.latestValue) {
            <div
              class="flex flex-col p-2 rounded-md hover:bg-indigo-50 transition-colors"
            >
              <span class="text-sm font-medium text-slate-500">{{
                attribute.name
              }}</span>
              <span class="text-base font-semibold text-slate-700">
                {{ attribute.latestValue | attributeValue: attribute.type }}
              </span>
            </div>
          }
        }

        @if (itemDetails().item.serial_number) {
          <div
            class="flex flex-col p-2 rounded-md hover:bg-indigo-50 transition-colors"
          >
            <span class="text-sm font-medium text-slate-500"
              >Serial Number</span
            >
            <span class="text-base font-semibold text-slate-700">
              {{ itemDetails().item.serial_number }}
            </span>
          </div>
        }

        @if (itemDetails().item.equipment_model) {
          <div
            class="flex flex-col p-2 rounded-md hover:bg-indigo-50 transition-colors"
          >
            <span class="text-sm font-medium text-slate-500">Make / Model</span>
            <span class="text-base font-semibold text-slate-700">
              {{ itemDetails().item.equipment_model | equipmentModel }}
            </span>
          </div>
        }

        @if (itemDetails().item.purchased_at) {
          <div
            class="flex flex-col p-2 rounded-md hover:bg-indigo-50 transition-colors"
          >
            <span class="text-sm font-medium text-slate-500"
              >Date Purchased</span
            >
            <span class="text-base font-semibold text-slate-700">
              {{ itemDetails().item.purchased_at | amDateFormat: dateFormat }}
            </span>
          </div>
        }

        @if (itemDetails().item.price) {
          <div
            class="flex flex-col p-2 rounded-md hover:bg-indigo-50 transition-colors"
          >
            <span class="text-sm font-medium text-slate-500"
              >Purchase Price</span
            >
            <span class="text-base font-semibold">
              {{ itemDetails().item.price | hlCurrency | async }}
            </span>
          </div>
        }

        @if (hasExtras()) {
          <div class="col-span-1 md:col-span-2 mt-2">
            <app-item-extras [item]="itemDetails().item"></app-item-extras>
          </div>
        }
      </div>

      @if (!hasAnyDetails()) {
        <div class="flex items-center justify-center py-6">
          <p class="text-slate-500 italic flex items-center gap-2">
            <mat-icon class="text-slate-400">info</mat-icon>
            No key details available
          </p>
        </div>
      }
    </div>
  `
})
export class KeyDetailsComponent {
  dateFormat = ConfigService.dates.dateFormat;
  itemDetails = input.required<ItemDetails>();

  hasExtras() {
    const item = this.itemDetails().item;
    return item.extras && Object.keys(item.extras).length > 0;
  }

  hasAnyDetails() {
    const item = this.itemDetails().item;
    const hasAttributes = this.itemDetails().summaryAttributes.some(
      (attr) => attr.latestValue
    );

    return (
      hasAttributes ||
      item.serial_number ||
      item.equipment_model ||
      item.purchased_at ||
      item.price ||
      this.hasExtras()
    );
  }
}
